import React, { useState } from "react";
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, FormControl, InputLabel,
  MenuItem, OutlinedInput, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip,
  XAxis, YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { formatMetric, useAnalyticsCatalogue, useAnalyticsQuery } from "../../../api/analytics";

interface Props {
  fleetId: number;
  from: string;
  to: string;
}

/**
 * Query builder over the semantic layer.
 *
 * The user composes with metric and dimension KEYS taken from the server
 * catalogue — never column names, never sort expressions. Nothing typed here
 * becomes part of a SQL statement.
 *
 * The catalogue also publishes `max_dimensions`, so the UI disables the fourth
 * checkbox instead of letting the user build a request the server will reject
 * with a 422.
 */
export const Explorateur: React.FC<Props> = ({ fleetId, from, to }) => {
  const { t } = useTranslation();
  const { catalogue, isLoading: catalogueLoading } = useAnalyticsCatalogue(fleetId);

  const [metrics, setMetrics] = useState<string[]>(["tco", "cost_per_km"]);
  const [dimensions, setDimensions] = useState<string[]>(["month"]);

  const maxDimensions = catalogue?.max_dimensions ?? 3;

  const { result, isLoading, error } = useAnalyticsQuery(
    fleetId,
    {
      metrics,
      dimensions,
      filters: { period: { from, to } },
      limit: 500,
    },
    metrics.length > 0
  );

  if (catalogueLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  const units = result?.units ?? {};
  const dimensionKey = dimensions[0];

  const chartData = (result?.rows ?? []).slice(0, 40).map((row) => ({
    label: String(row[dimensionKey] ?? "-"),
    value: Number(row[metrics[0]] ?? 0) / (units[metrics[0]] === "cents" ? 100 : 1),
  }));

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>{t("analytics.metrics", "Indicateurs")}</InputLabel>
              <Select
                multiple value={metrics} input={<OutlinedInput label={t("analytics.metrics", "Indicateurs")} />}
                onChange={(e) => setMetrics((e.target.value as string[]).slice(0, 10))}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(selected as string[]).map((key) => (
                      <Chip key={key} size="small" label={catalogue?.metrics.find((m) => m.key === key)?.label ?? key} />
                    ))}
                  </Stack>
                )}
              >
                {catalogue?.metrics.map((metric) => (
                  <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>{t("analytics.dimensions", "Dimensions")}</InputLabel>
              <Select
                multiple value={dimensions} input={<OutlinedInput label={t("analytics.dimensions", "Dimensions")} />}
                onChange={(e) => setDimensions((e.target.value as string[]).slice(0, maxDimensions))}
                renderValue={(selected) => (selected as string[]).join(", ")}
              >
                {catalogue?.dimensions.map((dimension) => (
                  <MenuItem
                    key={dimension.key}
                    value={dimension.key}
                    disabled={
                      dimensions.length >= maxDimensions && !dimensions.includes(dimension.key)
                    }
                  >
                    {dimension.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {t("analytics.maxDimensions", {
              defaultValue:
                "Maximum {{max}} dimensions. Au-dela, le resultat n'est plus lisible a l'ecran : utilisez un export.",
              max: maxDimensions,
            })}
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error">
          {(error as any)?.response?.data?.message ?? t("common.error", "Une erreur est survenue.")}
        </Alert>
      )}

      {result?.truncated && (
        <Alert severity="warning">
          {t("analytics.truncated", "Resultat tronque. Reduisez la periode ou le nombre de dimensions.")}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {chartData.length > 0 && dimensions.length === 1 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {catalogue?.metrics.find((m) => m.key === metrics[0])?.label} {t("analytics.by", "par")}{" "}
                  {catalogue?.dimensions.find((d) => d.key === dimensionKey)?.label}
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="value" name={catalogue?.metrics.find((m) => m.key === metrics[0])?.label} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  {t("analytics.results", "Resultats")}
                </Typography>
                <Chip size="small" label={`${result?.row_count ?? 0} ${t("analytics.rows", "lignes")}`} />
              </Stack>

              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    {dimensions.map((dimension) => (
                      <TableCell key={dimension}>
                        {catalogue?.dimensions.find((d) => d.key === dimension)?.label ?? dimension}
                      </TableCell>
                    ))}
                    {metrics.map((metric) => (
                      <TableCell key={metric} align="right">
                        {catalogue?.metrics.find((m) => m.key === metric)?.label ?? metric}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(result?.rows ?? []).map((row, index) => (
                    <TableRow key={index} hover>
                      {dimensions.map((dimension) => (
                        <TableCell key={dimension}>{String(row[dimension] ?? "-")}</TableCell>
                      ))}
                      {metrics.map((metric) => (
                        <TableCell key={metric} align="right">
                          {formatMetric(row[metric] as number, units[metric])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
};
